import type { Collect, SubmitPunchcardPayload } from '../types';
import { resolveCheckValue } from './formatters';

/**
 * Depois que um envio expira no timeout do cliente, decide se o ponto foi de fato
 * registrado no servidor procurando uma coleta recente correspondente — mesmo
 * tipo (entrada/saída), dentro de uma janela de tempo do instante tentado.
 *
 * PLATFORM-3336: o backend só rejeita reenvio com o `datetime` EXATO igual
 * (constraint de unicidade, confirmado contra o backend real: reenviar o mesmo
 * `datetime` dá 422 "There is already a collection on this date and time").
 * Um retry com `datetime` novo — o que o fluxo normal de envio sempre gera —
 * passa direto por essa constraint e duplicaria o registro. Esta é a única forma
 * segura de saber se um reenvio é necessário.
 */
export function findMatchingRecentCollect(
  collects: Collect[],
  payload: Pick<SubmitPunchcardPayload, 'check' | 'datetime'>,
  windowMs: number,
): Collect | undefined {
  // `collect.date`/`collect.time` vêm sem offset (ex.: "2026-09-22"/"16:04:37") —
  // o servidor ecoa o relógio de parede do dispositivo que enviou, sem fuso. Para
  // comparar de forma consistente, descartamos o offset de `payload.datetime`
  // também e interpretamos os dois lados como hora local do runtime. Comparar
  // um datetime COM offset contra um SEM offset via `new Date()` só bate por
  // coincidência quando o fuso do processo casa com o offset embutido — em CI/
  // Docker normalmente não casa, e a diferença de horas faria a janela nunca
  // combinar um ponto que de fato acabou de ser registrado.
  const wallClock = payload.datetime.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
  if (!wallClock) return undefined;
  const attemptedAt = new Date(`${wallClock[1]}T${wallClock[2]}`).getTime();
  if (Number.isNaN(attemptedAt)) return undefined;

  return collects.find((collect) => {
    if (resolveCheckValue(collect) !== payload.check) return false;
    const collectedAt = new Date(`${collect.date}T${collect.time}`).getTime();
    if (Number.isNaN(collectedAt)) return false;
    return Math.abs(collectedAt - attemptedAt) <= windowMs;
  });
}
